import React, { useState, useEffect, useRef } from 'react';
import { User, UserRole, ChatMessage } from '../types';
import { supabase } from '../supabase';
import { Send, Loader2, User as UserIcon, Building2, Check, CheckCheck } from 'lucide-react';

interface ChatProps {
  currentUser: User;
  targetId: string; // If current is client, target is gymId. If current is gym, target is clientId.
  targetName: string;
}

const Chat: React.FC<ChatProps> = ({ currentUser, targetId, targetName }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const gymId = currentUser.role === UserRole.GYM ? currentUser.gymId! : targetId;
  const clientId = currentUser.role === UserRole.CLIENT ? currentUser.id : targetId;

  useEffect(() => {
    fetchMessages();

    // Subscribe to new messages for this specific gym and client
    // Note: Supabase real-time filters are limited, so we subscribe to the gym's messages
    // and filter for the client in the callback to ensure we only get relevant updates.
    const channel = supabase
      .channel(`chat_${gymId}_${clientId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `gym_id=eq.${gymId}`,
        },
        (payload) => {
          const msg = payload.new as any;
          // Only add if it's for the current client
          if (msg.client_id === clientId) {
            setMessages((prev) => {
              // Avoid duplicates
              if (prev.find(m => m.id === msg.id)) return prev;
              return [
                ...prev,
                {
                  id: msg.id,
                  gymId: msg.gym_id,
                  clientId: msg.client_id,
                  senderRole: msg.sender_role as UserRole,
                  content: msg.content,
                  createdAt: msg.created_at,
                  isRead: msg.is_read || false,
                },
              ];
            });

            // If the message is from the other party, mark it as read immediately since chat is open
            if (msg.sender_role !== currentUser.role) {
              markAsRead(msg.id);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `gym_id=eq.${gymId}`,
        },
        (payload) => {
          const updatedMsg = payload.new as any;
          if (updatedMsg.client_id === clientId) {
            setMessages((prev) => 
              prev.map(m => m.id === updatedMsg.id ? { 
                ...m, 
                isRead: updatedMsg.is_read 
              } : m)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gymId, clientId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('gym_id', gymId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const fetchedMessages = (data || []).map((m) => ({
        id: m.id,
        gymId: m.gym_id,
        clientId: m.client_id,
        senderRole: m.sender_role as UserRole,
        content: m.content,
        createdAt: m.created_at,
        isRead: m.is_read || false,
      }));

      setMessages(fetchedMessages);

      // Mark unread messages from the other party as read
      const unreadFromOther = (data || []).filter(
        m => m.sender_role !== currentUser.role && !m.is_read
      );
      
      if (unreadFromOther.length > 0) {
        markAllAsRead(unreadFromOther.map(m => m.id));
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);
    } catch (err) {
      console.warn('Error marking message as read:', err);
    }
  };

  const markAllAsRead = async (messageIds: string[]) => {
    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .in('id', messageIds);
    } catch (err) {
      console.warn('Error marking messages as read:', err);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      console.log('Enviando mensaje...', { gym_id: gymId, client_id: clientId, role: currentUser.role });
      const { error } = await supabase.from('messages').insert({
        gym_id: gymId,
        client_id: clientId,
        sender_role: currentUser.role,
        content: content,
        is_read: false
      });

      if (error) {
        console.error('Error de Supabase al insertar mensaje:', error);
        throw error;
      }
      console.log('Mensaje enviado con éxito');
    } catch (err: any) {
      console.error('Error al enviar el mensaje:', err);
      alert('Error al enviar el mensaje: ' + (err.message || 'Error desconocido'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-brand" size={32} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[500px] glass-card overflow-hidden border-white/5">
      <div className="p-4 border-b border-white/5 bg-white/2 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand border border-brand/20">
          {currentUser.role === UserRole.GYM ? <UserIcon size={20} /> : <Building2 size={20} />}
        </div>
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight text-white">{targetName}</h3>
          <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest">Chat en vivo</p>
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-white/20">
            <Send size={48} className="mb-4 opacity-10" />
            <p className="font-display uppercase tracking-widest text-sm">Inicia la conversación</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderRole === currentUser.role;
            return (
              <div 
                key={msg.id} 
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    isMe 
                      ? 'bg-brand text-black rounded-tr-none' 
                      : 'bg-white/5 text-white border border-white/10 rounded-tl-none'
                  }`}
                >
                  <p className="leading-relaxed">{msg.content}</p>
                  <p className={`text-[8px] mt-1 uppercase font-bold tracking-widest flex items-center gap-1 ${isMe ? 'text-black/40' : 'text-white/20'}`}>
                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {isMe && (
                      <span className="flex ml-0.5">
                        {msg.isRead ? (
                          <CheckCheck size={11} className="text-sky-500" />
                        ) : (
                          <Check size={11} className="text-black/30" />
                        )}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={sendMessage} className="p-4 border-t border-white/5 bg-white/2 flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Escribe un mensaje..."
          className="flex-1 luxury-input py-3"
        />
        <button 
          type="submit"
          disabled={!newMessage.trim()}
          className="bg-brand text-black p-3 rounded-xl hover:scale-105 active:scale-95 disabled:opacity-50 transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)]"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
};

export default Chat;
